import React, { useState, useEffect, useRef } from "react";
import BillHeader from "./BillHeader";
import BillItemsTable from "./BillItemsTable";
import PaymentSummary from "./PaymentSummary";
import PaymentAllocation from "./PaymentAllocation";
import { FaCheck, FaPrint, FaSave, FaTrash, FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateItem,
  useDeleteItem,
  useUpdateItem,
} from "../../_helper/actions/mutation/items";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { useItemsFilter } from "../../_helper/actions/app/items";
import { useReactToPrint } from "react-to-print";
import PharmacyBillPrint from "./PharmacyBillPrint";
import AttachPrescription from "./AttachPrescription";
import { toTitleCase } from "../../_helper/utils/utils";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const CreateBill = () => {
  // State for prescription details
  const [prescription, setPrescription] = useState({});
  const [isEditable, setIsEditable] = useState(true); // Editable state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Deleted state
  const [discountType, setDiscountType] = useState("lumpSum"); // "lumpSum" or "percentage"
  const [discountValue, setDiscountValue] = useState(0);
  // New state for tracking deleted items
  const [deletedItems, setDeletedItems] = useState([]);
  const [splitPayments, setSplitPayments] = useState([]);
  // Dynamic calculation states
  const [subtotal, setSubtotal] = useState(0);
  const [totalTaxes, setTotalTaxes] = useState({
    cgst: 0,
    sgst: 0,
    igst: 0,
    totalGST: 0,
  });
  const [totalAfterDiscount, setTotalAfterDiscount] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [isLineDiscountApplied, setIsLineDiscountApplied] = useState(false);
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const navigate = useNavigate();

  // Create a ref for the print component.
  const printRef = useRef();

  // Configure react-to-print using the printRef.
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
   
      @page {
        size: 58mm auto; /* Change to 80mm if using a larger thermal printer */
        margin: 5mm;
      }
      @media print {
        body {
          font-family: 'Courier New', monospace;
          font-size: 10px;
          width: 58mm;
          margin: 0;
          padding: 0;
          -webkit-print-color-adjust: exact;
        }
      }
    
  `,
  });
  const pharmacy_id = searchParams.get("pharmacy_id");

  const { data: billData } = useItemsFilter(
    ApiEndPoint.PHARMACY,
    `?action=getPharmacyBill&id=${id}`,
    id !== "new" ? true : false
  );

  const calculateAgeFromDOB = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    if (
      today.getMonth() < birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age.toString();
  };

  // Include the new property is_prescription_required
  function convertBillingItems(items) {
    return items?.map((item) => ({
      description: "",
      old_quantity: item.quantity,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      cgstRate: item.cgst_rate,
      sgstRate: item.sgst_rate,
      igstRate: item.igst_rate,
      code: item.item_id,
      lineDiscountType: item.line_discount_type,
      lineDiscountValue: item.line_discount_value,
      batchNo: item.batch_no,
      itemName: item.items_name,
      batchId: item.batch_id,
      expiryDate: item.expiry_date,
      batchPrice: item.batch_price,
      sellingPrice: item.selling_price,
      is_prescription_required: item.is_prescription_required, // New property added
    }));
  }

  // When billData is available, pre-populate the prescription state.
  // console.log("Bill Data", billData);
  useEffect(() => {
    if (billData) {
      setSplitPayments(
        billData.payment_allocations.map((p) => ({
          ...p,
          amount: Number(p.amount) || 0,
        }))
      );
      setGrandTotal(billData?.payment_summary?.grandTotal);
      setDiscountType(billData?.payment_summary?.discountType);
      setDiscountValue(billData?.payment_summary?.discountValue);
      setSubtotal(billData?.payment_summary?.subtotal);
      setTotalTaxes(billData?.payment_summary?.totalTaxes);
      setPrescription({
        status: billData.status,
        selectedDoctor: {
          label: billData?.doctor?.doctor_name,
          value: billData?.doctor?.id,
        },
        doctor_name: billData?.doctor?.doctor_name,
        id: billData.patient_id.id,
        mobile: billData.patient_id.phone,
        dob: billData.patient_id.dob,
        age: calculateAgeFromDOB(billData.patient_id.dob),
        date: billData.date,
        gender: billData.patient_id.gender,
        selectedPatient: {
          label: billData.patient_id.full_name,
          value: billData.patient_id.id,
          user: billData.patient_id.user,
          is_parent: billData.patient_id.is_parent,
        },
        items: convertBillingItems(billData.billing_item),
      });
    }
  }, [billData]);

  // Update functions
  const updateItems = (updatedItems) => {
    setPrescription((prev) => ({ ...prev, items: updatedItems }));
  };

  // const updateHeader = (updatedHeader) => {
  //   setPrescription((prev) => ({
  //     ...prev,
  //     ...updatedHeader,
  //   }));
  // };

  // console.log("SP", splitPayments);
  const mutation = useCreateItem(ApiEndPoint.PHARMACY, ApiEndPoint.USERS);
  const update_mutation = useUpdateItem(ApiEndPoint.PHARMACY);
  const delete_mutation = useDeleteItem(ApiEndPoint.PHARMACY);

  // console.log("PRECRIPTION", prescription);

  // Recalculate subtotal whenever items change
  useEffect(() => {
    const newSubtotal = prescription?.items?.reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    setSubtotal(newSubtotal);
  }, [prescription?.items]);

  // console.log("Grand Total", grandTotal);

  // Recalculate taxes whenever items change
  useEffect(() => {
    const newTaxes = prescription?.items?.reduce(
      (taxes, item) => {
        const taxableValue = item.sellingPrice * item.quantity;
        const cgst = (taxableValue * item.cgstRate) / 100;
        const sgst = (taxableValue * item.sgstRate) / 100;
        const igst = (taxableValue * item.igstRate) / 100;

        return {
          cgst: taxes.cgst + cgst,
          sgst: taxes.sgst + sgst,
          igst: taxes.igst + igst,
          totalGST: taxes.totalGST + cgst + sgst + igst,
        };
      },
      { cgst: 0, sgst: 0, igst: 0, totalGST: 0 }
    );
    setTotalTaxes(newTaxes);
  }, [prescription.items]);

  // Recalculate total after discount whenever subtotal or discount changes
  useEffect(() => {
    const discountAmount =
      discountType === "percentage"
        ? (subtotal * discountValue) / 100
        : discountValue;
    const newTotalAfterDiscount = subtotal - discountAmount;
    setTotalAfterDiscount(newTotalAfterDiscount);
  }, [subtotal, discountType, discountValue]);

  // Recalculate grand total whenever total after discount or taxes change
  useEffect(() => {
    setGrandTotal(totalAfterDiscount + totalTaxes?.totalGST);
  }, [totalAfterDiscount, totalTaxes?.totalGST]);

  // console.log("Precriptions", prescription);

  // Payment allocation handler
  const handleCompletePayment = (payments) => {
    // console.log("Payments Received:", payments);
    toast.success(
      `Payment completed successfully:\n${JSON.stringify(payments, null, 2)}`
    );
  };

  console.log("Precription", prescription);

  const handleSave = (status) => {
    // Validate required fields: Mobile, Gender, DOB, and at least one bill item
    if (
      !prescription?.mobile ||
      !prescription?.items ||
      !prescription?.gender ||
      !prescription?.dob ||
      prescription.items.length === 0
    ) {
      toast.error(
        "Mobile number, Gender, Age and at least one bill item are required to save or submit the bill."
      );
      return;
    }

    // Validate: If any bill item requires a prescription and the bill is physically verified, throw a separate error
    if (
      prescription?.items?.some((item) => item.is_prescription_required) &&
      !prescription.is_physically_verified
    ) {
      toast.error(
        "Bill contains items that require a prescription. Physical verification cannot be completed. Please update the verification status or provide the required prescription."
      );
      return;
    }

    // Find the user with is_parent: true from prescription.patientOptions
    const parentUser = prescription.patientOptions?.find(
      (user) => user.is_parent === true
    );

    // Map items to the required billing_item structure
    const billingItems = prescription?.items?.map((item) => ({
      items_name: item?.itemName,
      old_quantity: item?.old_quantity || 0,
      quantity: item?.quantity,
      unit_price: item?.unitPrice,
      cgst_rate: item?.cgstRate,
      sgst_rate: item?.sgstRate,
      igst_rate: item?.igstRate,
      item_id: item?.code,
      line_discount_type: item?.lineDiscountType,
      line_discount_value: item?.lineDiscountValue,
      batch_no: item?.batchNo,
      batch_id: item?.batchId,
      expiry_date: item?.expiryDate,
      batch_price: item?.batchPrice,
      selling_price: item?.sellingPrice,
      is_prescription_required: item?.is_prescription_required, // Include when saving
      schedule: item.schedule,
    }));

    const deletedItem = deletedItems?.map((item) => ({
      items_name: item?.itemName,
      old_quantity: item?.old_quantity,
      quantity: item?.quantity,
      unit_price: item?.unitPrice,
      cgst_rate: item?.cgstRate,
      sgst_rate: item?.sgstRate,
      igst_rate: item?.igstRate,
      item_id: item?.code,
      line_discount_type: item?.lineDiscountType,
      line_discount_value: item?.lineDiscountValue,
      batch_no: item?.batchNo,
      batch_id: item?.batchId,
      expiry_date: item?.expiryDate,
      batch_price: item?.batchPrice,
      selling_price: item?.sellingPrice,
    }));

    // Determine the patient_id structure based on conditions
    let patientData = {};

    if (prescription?.selectedPatient?.isNewPatient === true) {
      // If there are no patient options, create a new patient object
      patientData = {
        full_name: toTitleCase(prescription.label || prescription.full_name),
        dob: prescription.dob,
        gender: prescription.gender,
        phone: prescription.mobile,
        is_parent: true,
        user: {
          first_name: prescription.full_name?.split(" ")[0] || "",
          last_name: prescription.full_name?.split(" ")[1] || "",
          email: `${prescription.mobile}@gmail.com`,
          mobile: prescription.mobile,
        },
      };
    } else if (parentUser && prescription.selectedPatient.isNewFamilyMember) {
      // If there's a parentUser and isNewFamilyMember is true, don't include id
      patientData = {
        full_name: toTitleCase(prescription.label || prescription.full_name),
        dob: prescription.dob,
        gender: prescription.gender,
        phone: prescription.mobile,
        is_parent: false,
        user: parentUser.user,
      };
    } else {
      // If there's an ID, include only id in the object

      patientData = prescription.id
        ? prescription.id
        : {
            id: prescription.id,
            full_name: toTitleCase(
              prescription.label || prescription.full_name
            ),
            dob: prescription.dob,
            gender: prescription.gender,
            phone: prescription.mobile,
            is_parent: false,
            user: parentUser ? parentUser.value || parentUser.user : {},
          };
    }

    if (id !== "new") {
      const update_payload = {
        action: "patchPharmacyBill",
        id: id,
        patient_id: patientData,
        date: new Date(prescription.date).toISOString(),
        billing_item: [...billingItems, ...deletedItem],
        doctor: {
          doctor_name: prescription?.selectedDoctor?.label,
          id: prescription?.selectedDoctor?.__isNew__
            ? null
            : prescription?.selectedDoctor?.value,
        },
        payment_summary: {
          discountValue: discountValue,
          discountType: discountType,
          subtotal: subtotal,
          totalTaxes: totalTaxes,
          grandTotal: grandTotal,
        },
        status: status,
        payment_allocation: splitPayments,
      };

      update_mutation.mutate(
        { data: update_payload },
        {
          onSuccess: () => {
            setDeletedItems([]);
            toast.success("Bill Updated");
          },
        }
      );
    } else {
      const payload = {
        pharmacy_id: pharmacy_id,
        action: "postPharmacyBill",
        billing_item: billingItems || [],
        date: new Date(prescription.date).toISOString(),
        status: status,
        patient_id: patientData,
        doctor: {
          doctor_name: prescription?.selectedDoctor?.label,
          id: prescription?.selectedDoctor?.__isNew__
            ? null
            : prescription?.selectedDoctor?.value,
        },
        payment_summary: {
          discountValue: discountValue,
          discountType: discountType,
          subtotal: subtotal,
          totalTaxes: totalTaxes,
          grandTotal: grandTotal,
        },
        payment_allocation: splitPayments,
      };

      mutation.mutate(payload, {
        onSuccess: (data) => {
          toast.success("Bill Saved!");
          navigate(`/new/pharmacy/bill/create/${data?.data?.id}`);
        },
      });
    }
  };

  // Delete bill
  const handleDelete = () => {
    delete_mutation.mutate(
      {
        data: {
          action: "delPharmacyBill",
          id: id,
        },
      },
      {
        onSuccess: () => {
          navigate("/new/pharmacy");
          toast.success("Bill Deleted successfully !");
        },
      }
    );
  };

  // Check form validity: Mobile No and at least one bill item required
  const isFormValid =
    prescription?.mobile &&
    prescription?.items &&
    prescription.items.length > 0;

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-6 relative">
      {/* Header with Back Button */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => {
            navigate("/new/pharmacy");
          }}
          className="mr-4 text-blue-800 font-bold py-2 px-4 rounded inline-flex items-center"
        >
          <FaArrowLeft size={18} />
          <span className="ml-2">Back</span>
        </button>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Create Bill</h2>
      {/* Bill Header */}
      <BillHeader
        prescription={prescription}
        setPrescription={setPrescription}
        isEdit={id !== "new"}
        isPublished={prescription.status === "published"}
        pharmacy_id={pharmacy_id}
      />

      {/* Items Table */}
      <BillItemsTable
        pharmacy_id={pharmacy_id}
        items={prescription?.items}
        onUpdateItems={updateItems}
        isEditable={isEditable}
        onLineDiscountChange={setIsLineDiscountApplied}
        deletedItems={deletedItems}
        setDeletedItems={setDeletedItems}
        isPublished={prescription.status === "published"}
      />

      {/* Conditionally show AttachPrescription if any item requires a prescription */}
      {prescription?.items?.some((item) => item.is_prescription_required) && (
        <AttachPrescription
          setPrescription={setPrescription}
          prescription={prescription}
        />
      )}

      {/* Payment Summary */}
      <PaymentSummary
        items={prescription?.items}
        isLineDiscountApplied={isLineDiscountApplied}
        subtotal={subtotal}
        discountType={discountType}
        discountValue={discountValue}
        setDiscountType={setDiscountType}
        setDiscountValue={setDiscountValue}
        totalGST={totalTaxes?.totalGST}
        grandTotal={grandTotal}
        setGrandTotal={setGrandTotal}
        isPublished={prescription.status === "published"}
      />
      {/* Payment Allocation */}
      <PaymentAllocation
        total={grandTotal}
        onPaymentComplete={handleCompletePayment}
        setSplitPayments={setSplitPayments}
        splitPayments={splitPayments}
      />

      {/* Validation Message */}
      {!isFormValid && (
        <div className="text-red-500 text-sm mb-4">
          Mobile number Age Gender and at least one bill item are required.
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 flex flex-wrap items-center justify-center gap-4 bg-white p-4 rounded-lg shadow-md">
        {prescription.status !== "published" && (
          <button
            onClick={() => handleSave("draft")}
            className={`flex items-center justify-center w-full md:w-auto space-x-2 px-6 py-3 rounded-lg transition ${
              isEditable && isFormValid
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-400 text-gray-700 cursor-not-allowed"
            }`}
            disabled={!isEditable || !isFormValid || mutation.isLoading}
          >
            {mutation.isLoading ? (
              <span>Saving...</span>
            ) : (
              <>
                <FaSave size={18} />
                <span>Save</span>
              </>
            )}
          </button>
        )}

        <button
          onClick={() => handleSave("published")}
          className={`flex items-center justify-center w-full md:w-auto space-x-2 px-6 py-3 rounded-lg transition ${
            isEditable && isFormValid
              ? "bg-green-600 text-white hover:bg-green-700"
              : "bg-gray-400 text-gray-700 cursor-not-allowed"
          }`}
          disabled={!isEditable || !isFormValid || update_mutation.isLoading}
        >
          {update_mutation.isLoading ? (
            <span>Submitting...</span>
          ) : (
            <>
              <FaCheck size={18} />
              <span>Submit</span>
            </>
          )}
        </button>

        {prescription.status === "draft" && (
          <button
            onClick={() => {
              setIsDeleteModalOpen(true);
            }}
            className="flex items-center justify-center w-full md:w-auto space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition"
          >
            <FaTrash size={18} />
            <span>Delete</span>
          </button>
        )}

        {prescription.status === "published" && (
          <button
            onClick={handlePrint}
            className="flex items-center justify-center w-full md:w-auto space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700 transition"
          >
            <FaPrint size={18} />
            <span>Print</span>
          </button>
        )}
      </div>
      {/* Hidden Print Component */}
      <div className="hidden">
        <PharmacyBillPrint ref={printRef} billData={billData} />
      </div>
      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default CreateBill;
